import { DateTime } from "luxon"
import useSetTimeout from "../hooks/useSetTimeout"
import timeAgo from "../util/timeAgo"
import { useState } from "react"
import "./RelativeTime.css"

/**
 * Formats relative time strings
 * 
 * NOTE: Currently supports English only. To add internationalization:
 * 1. Install i18n library (e.g., react-i18next)
 * 2. Replace hardcoded strings below with translation keys:
 *    - 'Yesterday at {time}'
 *    - 'Today at {time}'
 *    - 'Tomorrow at {time}'
 *    - '{weekday} at {time}'
 * 3. Use Luxon's setLocale() for date formatting
 * 
 * @param dt - DateTime to format
 * @param now - Current time (defaults to DateTime.now())
 * @returns Formatted relative time string
 */
const relTime = (dt:DateTime, now?:DateTime) => {
  // Every comparison below is false for an invalid DateTime (NaN compares false both ways),
  // so without this guard the function falls through to the ISO branch and renders the
  // literal string "null at Invalid DateTime" to the user.
  if (!dt.isValid) return 'Unknown date'

  now = now || DateTime.now()
  const today = now.startOf('day')
  const yesterday = today.minus({day:1})
  if (dt >= yesterday && dt < today) {
    return 'Yesterday at ' + dt.toFormat('t')
  }

  const tomorrow = today.plus({day:1})
  if (dt >= today && dt < tomorrow) {
    if (dt < now || dt > now.plus({hours:2})) {
      return 'Today at ' + dt.toFormat('t')
    }
    return timeAgo(dt)
  }
  if (dt >= tomorrow && dt < tomorrow.plus({day:1})) {
    return 'Tomorrow at ' + dt.toFormat('t')
  }

  const weekAgo = now.minus({day:5})
  const weekAway = now.plus({day:5})
  if (dt >= weekAgo && dt < weekAway) {
    return dt.toFormat('cccc') + ' at ' + dt.toFormat('t')
  }
  return dt.toISODate() + ' at ' + dt.toFormat('t')
}

const toDateTime = (value:string|Date|DateTime) => {
  if (typeof(value) === 'string') return DateTime.fromISO(value)  // BUG FIX: Use strict equality
  if (value instanceof Date) return DateTime.fromJSDate(value)
  if (value instanceof DateTime) return value
  return DateTime.fromISO('')
}

interface Props {
  value: string|Date|DateTime
}
const RelativeTime = ({value}:Props) => {
  const [flag, setFlag] = useState(false)
  const refresh = () => setFlag(!flag)
  const {setTimeout} = useSetTimeout()
  setTimeout(()=>refresh(), 1000)

  const dt = toDateTime(value)
  // `is-invalid` was used here, but it only sets border-color and this span has no border,
  // so the invalid state rendered nothing at all. Measured in design-lab/is-invalid-census.html.
  const className = dt.isValid ? 'time-ago' : 'time-ago time-ago-invalid'
  return <span className={className} title={dt.isValid ? undefined : 'This date could not be read'}>{relTime(dt)}</span>
}
export default RelativeTime